import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db } from '@/config/firebase'
import { 
  collection, 
  doc, 
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  deleteDoc,
  query, 
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { useAuthStore } from './auth'
import { useProductStore } from './products'
import type { Rule, RuleGroup } from '@/types/rules'

export const useRulesStore = defineStore('rules', () => {
  const authStore = useAuthStore()
  const productStore = useProductStore()
  
  const ruleGroups = ref<RuleGroup[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const savingGroups = ref<Record<string, boolean>>({})
  const originalStates = ref<Record<string, string>>({})
  const saveSuccess = ref<Record<string, boolean>>({})

  // Sort rule groups by order
  const sortedRuleGroups = computed(() => {
    return [...ruleGroups.value].sort((a, b) => a.order - b.order)
  })

  // Reset success state after a delay
  const resetSaveSuccess = (groupId: string) => {
    setTimeout(() => {
      saveSuccess.value[groupId] = false
    }, 2000) // Reset after 2 seconds
  }

  // Computed values from products
  const availableProductTypes = computed(() => {
    const types = new Set(productStore.products
      .map(p => p.product_type)
      .filter(type => type && type.trim() !== '')
    )
    return Array.from(types).sort()
  })

  const availableTags = computed(() => {
    const tags = new Set(productStore.products.flatMap(p => p.tags || []))
    return Array.from(tags).sort()
  })

  // Get user's org_id from their document
  const getUserOrgId = async (): Promise<string> => {
    if (!authStore.user) throw new Error('No authenticated user')
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    if (!userDoc.exists()) throw new Error('User document not found')
    return userDoc.data().org_id
  }

  // Load rules for current org
  const loadRules = async () => {
    if (!authStore.user) return

    loading.value = true
    error.value = null
    
    try {
      const org_id = await getUserOrgId()
      console.log('Loading rules for org_id:', org_id) // Debug log
      
      const rulesRef = collection(db, 'rules')
      const q = query(rulesRef, where('org_id', '==', org_id))
      const snapshot = await getDocs(q)
      
      console.log('Found rules:', snapshot.docs.length) // Debug log
      
      const groups = snapshot.docs.map(doc => {
        const data = doc.data()
        
        return {
          id: doc.id,
          name: data.name || '',
          rules: Array.isArray(data.rules) ? data.rules : [],
          logicalOperator: data.logical_operator || 'AND',
          outcome: data.outcome || 'active',
          isActive: data.is_active ?? true,
          order: data.order ?? 0
        }
      })
      
      ruleGroups.value = groups
      // Store initial state for each group
      groups.forEach(group => {
        originalStates.value[group.id] = JSON.stringify(group)
      })
    } catch (err) {
      console.error('Error loading rules:', err)
      error.value = 'Failed to load rules'
      throw err // Re-throw to see full error in console
    } finally {
      loading.value = false
    }
  }

  // Save rule group to Firestore
  const saveRuleGroup = async (groupId: string) => {
    if (!authStore.user) throw new Error('No authenticated user')
    if (savingGroups.value[groupId]) return
    
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (!group) throw new Error('Rule group not found')

    savingGroups.value[groupId] = true
    saveSuccess.value[groupId] = false
    
    try {
      const org_id = await getUserOrgId()
      const docRef = doc(db, 'rules', groupId)
      
      await setDoc(docRef, {
        org_id,
        name: group.name,
        rules: group.rules,
        logical_operator: group.logicalOperator,
        outcome: group.outcome,
        is_active: group.isActive,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })

      // Update original state and show success
      originalStates.value[groupId] = JSON.stringify(group)
      saveSuccess.value[groupId] = true
      resetSaveSuccess(groupId)
    } catch (err) {
      console.error('Error saving rule group:', err)
      throw new Error('Failed to save rule group')
    } finally {
      savingGroups.value[groupId] = false
    }
  }

  // Delete rule group
  const deleteRuleGroup = async (groupId: string) => {
    if (!authStore.user) throw new Error('No authenticated user')
    
    try {
      await deleteDoc(doc(db, 'rules', groupId))
      ruleGroups.value = ruleGroups.value.filter(g => g.id !== groupId)
    } catch (err) {
      console.error('Error deleting rule group:', err)
      throw new Error('Failed to delete rule group')
    }
  }

  // Get next available order number
  const getNextOrder = (): number => {
    if (ruleGroups.value.length === 0) return 0
    return Math.max(...ruleGroups.value.map(g => g.order)) + 1
  }

  // Add new rule group
  const addRuleGroup = async (name: string, outcome: 'active' | 'inactive' = 'active') => {
    if (!authStore.user) throw new Error('No authenticated user')

    try {
      const org_id = await getUserOrgId()
      const rulesRef = collection(db, 'rules')
      const order = getNextOrder()
      
      const docRef = await addDoc(rulesRef, {
        org_id,
        name,
        rules: [],
        logical_operator: 'AND',
        outcome,
        is_active: true,
        order,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })

      const group = {
        id: docRef.id,
        name,
        rules: [],
        logicalOperator: 'AND' as const,
        outcome,
        isActive: true,
        order
      }

      ruleGroups.value.push(group)
      // Store initial state for new group
      originalStates.value[group.id] = JSON.stringify(group)
      return group
    } catch (err) {
      console.error('Error creating rule group:', err)
      throw new Error('Failed to create rule group')
    }
  }

  // Add rule to group
  const addRule = (groupId: string, rule: Rule) => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (group) {
      group.rules.push(rule)
      // Update original state to include the new rule
      originalStates.value[groupId] = JSON.stringify(group)
    }
  }

  // Delete rule from group
  const deleteRule = (groupId: string, ruleIndex: number) => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (group && ruleIndex >= 0 && ruleIndex < group.rules.length) {
      group.rules.splice(ruleIndex, 1)
    }
  }

  // Toggle rule group active state
  const toggleRuleGroup = (groupId: string) => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (group) {
      group.isActive = !group.isActive
    }
  }

  // Update rule
  const updateRule = (groupId: string, ruleIndex: number, updates: Partial<Rule>) => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (group && ruleIndex >= 0 && ruleIndex < group.rules.length) {
      Object.assign(group.rules[ruleIndex], updates)
    }
  }

  // Update rule group
  const updateRuleGroup = (groupId: string, updates: Partial<RuleGroup>) => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (group) {
      Object.assign(group, updates)
    }
  }

  // Check if group has unsaved changes
  const hasUnsavedChanges = (groupId: string): boolean => {
    const group = ruleGroups.value.find(g => g.id === groupId)
    if (!group || !originalStates.value[groupId]) return false
    return originalStates.value[groupId] !== JSON.stringify(group)
  }

  // Check if group is being saved
  const isSaving = (groupId: string): boolean => {
    return !!savingGroups.value[groupId]
  }

  // Check if group was just saved successfully
  const isSaveSuccess = (groupId: string): boolean => {
    return !!saveSuccess.value[groupId]
  }

  // Store initial state when loading or creating groups
  const storeInitialState = (group: RuleGroup) => {
    originalStates.value[group.id] = JSON.stringify(group)
  }

  // Store initial states after loading
  const loadRulesSuccess = (groups: RuleGroup[]) => {
    groups.forEach(storeInitialState)
  }

  return {
    ruleGroups,
    sortedRuleGroups,
    loading,
    error,
    availableProductTypes,
    availableTags,
    loadRules,
    saveRuleGroup,
    deleteRuleGroup,
    addRuleGroup,
    addRule,
    deleteRule,
    toggleRuleGroup,
    updateRule,
    updateRuleGroup,
    hasUnsavedChanges,
    isSaving,
    isSaveSuccess
  }
})